import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import ScrollDots from '../common/ScrollDots';
import StaffPartyPopup from '../common/StaffPartyPopup';
import FathersDayPopup from '../common/FathersDayPopup';
import FourthOfJulyPopup from '../common/FourthOfJulyPopup';
import FiveYearsPopup from '../common/FiveYearsPopup';
import TapasTimePopup from '../common/TapasTimePopup';

interface LayoutProps {
  children: ReactNode;
}

// Popups each render their own full-screen dark overlay, so only one may be
// mounted at a time or the overlays stack and darken. Whichever window is
// active wins, in this priority order (top = highest priority).
const getActivePopup = (isHomePage: boolean) => {
  if (typeof window === 'undefined') return null;

  const now = Date.now();

  // "Tapas time is back" teaser - home page only, no end date yet.
  if (isHomePage) return 'tapasTime';

  const today = new Date();
  const isStaffPartyDay =
    today.getFullYear() === 2026 && today.getMonth() === 0 && today.getDate() === 20;
  if (isStaffPartyDay) return 'staffParty';

  // 5 Years anniversary promo - through end of today, July 1, 2026 PT
  if (now <= Date.UTC(2026, 6, 2, 7, 0, 0)) return 'fiveYears';

  // 4th of July closure announcement - through end of July 4, 2026 PT
  if (now <= Date.UTC(2026, 6, 5, 7, 0, 0)) return 'fourthOfJuly';

  // Father's Day promo - through end of Father's Day (Sun June 21, 2026) PT
  if (now <= Date.UTC(2026, 5, 22, 7, 0, 0)) return 'fathersDay';

  return null;
};

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const activePopup = getActivePopup(isHomePage);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen bg-background overflow-hidden">
      {activePopup === 'tapasTime' && <TapasTimePopup />}
      {activePopup === 'staffParty' && <StaffPartyPopup />}
      {activePopup === 'fiveYears' && <FiveYearsPopup />}
      {activePopup === 'fourthOfJuly' && <FourthOfJulyPopup />}
      {activePopup === 'fathersDay' && <FathersDayPopup />}

      {/* Premium background elements */}
      <div className="fixed inset-0 bg-paper-texture opacity-40 pointer-events-none z-0"></div>
      <div className="fixed top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      <div className="fixed bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      
      {/* Sticky Header */}
      <Header />
      
      {/* Main Content with page transitions */}
      <AnimatePresence mode="wait">
        <motion.main 
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="relative z-10"
        >
        {children}
        </motion.main>
      </AnimatePresence>
      
      {/* Vertical ScrollDots - only show on home page */}
      {isHomePage && (
        <motion.div
          className="hidden md:block fixed md:right-8 top-1/2 transform -translate-y-1/2 z-50"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <ScrollDots />
        </motion.div>
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout; 